import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CalendarDays, Camera, MapPin, Plus, UserRound, X } from "lucide-react";
import MemberGate from "../components/MemberGate";
import { ErrorMessage, Loading } from "../components/Loading";
import { api } from "../lib/api";
import { cropStyle } from "../lib/crop";
import { useApi } from "../hooks/useApi";

function CreateReunion({ authenticated, canContribute, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", date: "", location: "", organizer: "", contact: "", description: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const dialog = useRef(null);

  useEffect(() => dialog.current?.focus(), []);
  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const result = await api("/api/reunions", { method: "POST", body: JSON.stringify(form) });
      onCreated(result.reunion);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-panel" role="dialog" aria-modal="true" aria-labelledby="create-reunion-title" tabIndex="-1" ref={dialog}>
        <button className="icon-button modal-close" type="button" onClick={onClose} aria-label="Close"><X aria-hidden="true" /></button>
        {!authenticated ? (
          <MemberGate compact onSuccess={() => window.location.reload()} />
        ) : !canContribute ? (
          <>
            <p className="kicker">Read-only member access</p>
            <h2 id="create-reunion-title">Use your individual account.</h2>
            <p className="modal-intro">The shared class passphrase is for viewing. Sign out and use an activated individual account to create reunion pages.</p>
          </>
        ) : (
          <>
            <p className="kicker">Create a class gathering</p>
            <h2 id="create-reunion-title">Give the reunion a home.</h2>
            <p className="modal-intro">Start with the essentials. You can return to edit the details, captions, cover, and photographs; classmates can still add their own frames to the shared story.</p>
            <form className="form-grid" onSubmit={submit}>
              <label className="form-field form-field--wide">Reunion title<input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Bay Area summer lunch" /></label>
              <label className="form-field">Date<input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></label>
              <label className="form-field">Place<input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="San Jose, California" /></label>
              <label className="form-field">Organizer<input value={form.organizer} onChange={(e) => setForm({ ...form, organizer: e.target.value })} /></label>
              <label className="form-field">Contact<input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Email or WhatsApp" /></label>
              <label className="form-field form-field--wide">What should classmates know?<textarea required rows="5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Plans, families welcome, what to bring…" /></label>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <button className="button button--coral" disabled={saving} type="submit">{saving ? "Creating…" : "Create reunion page"} <ArrowRight aria-hidden="true" /></button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}

export default function Reunions({ authenticated, canContribute, version, onCreated }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(searchParams.get("create") === "1");
  const loader = useCallback(() => api(`/api/reunions?v=${version}`), [version]);
  const { data, error, loading, reload } = useApi(loader, [loader]);
  const reunions = useMemo(() => [...(data?.reunions || [])].sort((a, b) => b.year - a.year), [data]);

  function closeCreate() {
    setShowCreate(false);
    searchParams.delete("create");
    setSearchParams(searchParams, { replace: true });
  }

  return (
    <>
      <section className="page-hero page-hero--reunions">
        <div>
          <p className="kicker">Four milestones and counting</p>
          <h1>Every reunion<br />is a new chapter.</h1>
        </div>
        <div className="page-hero__aside">
          <p>From Dubai in 2005 to Kochi in 2025, the archive follows a class that keeps finding its way back into the same room.</p>
          {(!authenticated || canContribute) && <button className="button button--coral" type="button" onClick={() => setShowCreate(true)}><Plus aria-hidden="true" /> Create a local reunion</button>}
        </div>
      </section>

      <section className="reunion-timeline section-wrap">
        {loading && <Loading />}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {reunions.map((reunion, index) => (
          <article className="reunion-row" key={reunion.id} style={{ "--i": index }}>
            <div className="reunion-row__year">{reunion.year}</div>
            <Link className="reunion-row__image" to={`/reunions/${reunion.id}`}>
              {reunion.cover ? <img src={reunion.cover} alt="" style={cropStyle(reunion.coverCrop)} /> : <div className="image-placeholder"><Camera aria-hidden="true" /></div>}
            </Link>
            <div className="reunion-row__story">
              <p className="kicker">{reunion.eyebrow}</p>
              <h2><Link to={`/reunions/${reunion.id}`}>{reunion.title}</Link></h2>
              {reunion.kind === "local" && reunion.createdBy && <p className="reunion-row__creator"><UserRound aria-hidden="true" /> Created by <strong>{reunion.createdBy.name}</strong></p>}
              <p>{reunion.description}</p>
              <div className="reunion-row__facts">
                <span><CalendarDays aria-hidden="true" /> {reunion.date}</span>
                <span><MapPin aria-hidden="true" /> {reunion.location}</span>
              </div>
              <Link className="text-link" to={`/reunions/${reunion.id}`}>Open this chapter <ArrowRight aria-hidden="true" /></Link>
            </div>
          </article>
        ))}
      </section>

      <section className="reunion-cta section-wrap">
        <p className="kicker">Small gathering, full story</p>
        <h2>Three classmates at breakfast counts.</h2>
        <p>Local pages make room for spontaneous meetups, branch gatherings, family picnics, and photographs that would otherwise disappear into a chat thread.</p>
        {(!authenticated || canContribute)
          ? <button className="button button--ink" type="button" onClick={() => setShowCreate(true)}>Start the page <Plus aria-hidden="true" /></button>
          : <p className="story-account-note">Shared passphrase access is read-only. Use an individual account to create a reunion page.</p>}
      </section>

      {showCreate && (
        <CreateReunion
          authenticated={authenticated}
          canContribute={canContribute}
          onClose={closeCreate}
          onCreated={async () => { onCreated(); await reload(); closeCreate(); }}
        />
      )}
    </>
  );
}
